import { useEffect, useState, useCallback } from "react";
import { apiFetch } from "../context/AuthContext";

const B    = "#2C1A0E";
const ROSE = "#C9847A";

const SKUS = ["W01","W02","W03","W04","M01","M02","M03","M04","K01","K02"];
const SKU_NAMES = {
  W01:"Hindi Text Tote Bag", W02:"Embroidered Pendant", W03:"iPhone Cover Insert",
  W04:"Embroidered Diary (W)", M01:"Embroidered Shirt", M02:"Embroidered Diary (M)",
  M03:"Embroidered Magnet", M04:"NFC Card", K01:"Kids Photo Frame", K02:"Alphabet Motif",
};
const SKU_CAT = {
  W01:"Women",W02:"Women",W03:"Women",W04:"Women",
  M01:"Men",M02:"Men",M03:"Men",M04:"Men",
  K01:"Kids",K02:"Kids",
};
const CAT_COLOR = { Women: ROSE, Men: "#2C6E49", Kids: "#F4A261" };

export default function InventoryPage() {
  const [products,  setProducts]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [catFilter, setCatFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    const r = await apiFetch("/api/products");
    if (r.ok) setProducts(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = catFilter === "All"
    ? products
    : products.filter(p => SKU_CAT[p.sku_id] === catFilter);

  const totalStock = products.reduce((s, p) => s + (p.stock_on_hand || 0), 0);
  const lowStock   = products.filter(p => (p.stock_on_hand || 0) < 3).length;
  const outOfStock = products.filter(p => (p.stock_on_hand || 0) === 0).length;

  return (
    <div style={{ padding: "28px 28px 80px", fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',Georgia,serif",
                       color: B, fontSize: 26, fontWeight: 700, margin: 0 }}>
            📦 Inventory
          </h1>
          <p style={{ color: "#888", fontSize: 13, marginTop: 4 }}>
            Live stock from Shopify · synced every 6 hours
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["All","Women","Men","Kids"].map(c => (
            <button key={c} onClick={() => setCatFilter(c)} style={{
              background: catFilter === c ? B : "#F0EBE3",
              color: catFilter === c ? "#FAF7F2" : B,
              border: "none", borderRadius: 6, padding: "6px 14px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}>{c}</button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 28, flexWrap: "wrap" }}>
        {[
          { label: "Total Units", value: totalStock },
          { label: "SKUs Tracked", value: products.length },
          { label: "Low Stock (<3)", value: lowStock, color: lowStock > 0 ? "#92400E" : "#15803D" },
          { label: "Out of Stock", value: outOfStock, color: outOfStock > 0 ? "#DC2626" : "#15803D" },
        ].map(({ label, value, color }) => (
          <div key={label} style={kpiCard}>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: color || B }}>{value}</div>
          </div>
        ))}
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 13 }}>Loading…</div>}

      {/* Product cards grid */}
      {!loading && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
          {filtered.map(p => {
            const stock  = p.stock_on_hand || 0;
            const cat    = SKU_CAT[p.sku_id] || "Other";
            const status = stock === 0 ? "out" : stock < 3 ? "low" : "ok";
            const statusConfig = {
              out: { label: "Out of Stock", bg: "#FEE2E2", color: "#DC2626" },
              low: { label: "Low Stock",    bg: "#FEF9C3", color: "#854D0E" },
              ok:  { label: "In Stock",     bg: "#DCFCE7", color: "#15803D" },
            }[status];
            return (
              <div key={p._id || p.sku_id} style={{
                background: "#fff", border: "1px solid #EDE0D4",
                borderRadius: 12, padding: "16px", position: "relative",
                borderTop: `3px solid ${CAT_COLOR[cat] || "#ccc"}`,
              }}>
                {/* Category badge */}
                <div style={{
                  position: "absolute", top: 12, right: 12,
                  background: CAT_COLOR[cat] + "22", color: CAT_COLOR[cat],
                  borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700,
                }}>{cat}</div>

                {/* SKU */}
                <div style={{ fontSize: 10, color: "#aaa", fontWeight: 700,
                              letterSpacing: 1, marginBottom: 4 }}>{p.sku_id}</div>

                {/* Name */}
                <div style={{ fontWeight: 700, color: B, fontSize: 13,
                              marginBottom: 12, lineHeight: 1.3 }}>
                  {SKU_NAMES[p.sku_id] || p.name || p.title}
                </div>

                {/* Stock count — big */}
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 10 }}>
                  <span style={{ fontSize: 36, fontWeight: 700, color: statusConfig.color,
                                 lineHeight: 1 }}>{stock}</span>
                  <span style={{ fontSize: 12, color: "#888" }}>units</span>
                </div>

                {/* Status badge */}
                <div style={{
                  background: statusConfig.bg, color: statusConfig.color,
                  borderRadius: 6, padding: "4px 10px", fontSize: 11,
                  fontWeight: 600, display: "inline-block",
                }}>{statusConfig.label}</div>

                {/* Price */}
                {p.price && (
                  <div style={{ marginTop: 10, fontSize: 12, color: "#888" }}>
                    ₹{p.price.toLocaleString("en-IN")} · {p.type}
                  </div>
                )}

                {/* Shopify status */}
                {p.status && (
                  <div style={{ marginTop: 4, fontSize: 11, color: p.status === "active" ? "#15803D" : "#aaa" }}>
                    Shopify: {p.status}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div style={{ color: "#aaa", fontSize: 13 }}>
          No products found. Run a Shopify sync to pull live inventory.
        </div>
      )}
    </div>
  );
}

const kpiCard = { background:"#fff", border:"1px solid #EDE0D4", borderRadius:12,
                  padding:"14px 18px", flex:1, minWidth:120 };
